import React from "react";
import { motion } from "framer-motion";
import {
  Pill,
  HeartPulse,
  FileCheck,
  FlaskRound,
} from "lucide-react";

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.25 },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Formulations() {
  return (
    <div className="w-full">
      {/* Hero Banner */}
      <div
        className="relative h-[300px] md:h-[400px] w-full bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/formulations-banner.webp')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <h1 className="relative text-4xl md:text-5xl font-bold text-white text-center">
          Finished Dosage Formulations
        </h1>
      </div>

      {/* Content Section with Motion */}
      <motion.div
        className="max-w-6xl mx-auto px-4 py-12"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={container}
      >
        {/* Intro */}
        <motion.p className="text-lg text-gray-700 mb-10" variants={fadeInUp}>
          At <span className="font-semibold text-[#0d47a1]">MAC LABS LTD</span>, we
          develop and manufacture finished dosage formulations that meet the diverse
          therapeutic needs of global healthcare. With a strong focus on quality,
          safety, and efficacy, our formulations are designed to improve patient
          outcomes and support better healthcare access worldwide.
        </motion.p>

        {/* Capabilities */}
        <motion.h2 className="text-2xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
          Our Capabilities
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Animated Cards */}
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Pill className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Dosage Forms</h3>
            <p className="text-gray-600">
              Tablets, capsules, liquids, and other advanced delivery systems.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <HeartPulse className="w-8 h-8 text-red-500 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Therapeutic Segments
            </h3>
            <p className="text-gray-600">
              Covering cardiovascular, anti-infectives, anti-inflammatory,
              gastrointestinal, CNS, dermatology, and more.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <FileCheck className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Regulatory Compliance
            </h3>
            <p className="text-gray-600">
              Developed in cGMP-certified facilities, ensuring global acceptance and trust.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <FlaskRound className="w-8 h-8 text-purple-500 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Research & Development
            </h3>
            <p className="text-gray-600">
              Driving innovation in drug delivery, enhancing bioavailability, stability, and
              patient convenience.
            </p>
          </motion.div>
        </div>

        {/* Why Choose Us */}
        <motion.h2 className="text-2xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
          Why Choose MAC LABS Formulations?
        </motion.h2>
        <motion.ul
          className="list-disc list-inside text-gray-600 space-y-2 mb-10"
          variants={fadeInUp}
        >
          <li>Proven expertise in generic and specialty formulations.</li>
          <li>
            Flexible manufacturing capacity – from pilot scale to commercial production.
          </li>
          <li>
            Stringent quality control backed by world-class analytical infrastructure.
          </li>
          <li>
            Patient-centric innovation – prioritizing safety, affordability, and
            accessibility.
          </li>
        </motion.ul>

        {/* Closing Statement */}
        <motion.p className="text-gray-700 font-medium" variants={fadeInUp}>
          At <span className="font-semibold">MAC LABS LTD</span>, we believe that
          healthcare begins with trust. Our formulations are crafted with the highest
          standards so patients receive effective, safe, and reliable medicines—every
          time.
        </motion.p>
      </motion.div>
    </div>
  );
}
